import { useSuspenseQuery } from '@apollo/client';
import React, { Suspense } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { CopyTextButton } from '@eluve/blocks';
import {
  Heading,
  Icon,
  LinkButton,
  LoadingIndicator,
  Stack,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import {
  OpenInAppButton,
  OpenInReviewButton,
  TabNav,
} from '@eluve/smart-blocks';

import { getLangfuseSessionUrlForAppointment } from '../../utils/get-langfuse-url';

const appointmentNameQuery = graphql(`
  query appointmentName($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
      __typename
      id
      name
      createdAt
    }
  }
`);

export const AdminAppointmentOutlet: React.FC<{
  langfuseBaseUrl?: string | null;
  langfuseProjectId?: string | null;
}> = ({ langfuseBaseUrl, langfuseProjectId }) => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const {
    data: { appointmentsByPk: appointment },
  } = useSuspenseQuery(appointmentNameQuery, {
    variables: { tenantId, appointmentId },
  });

  const datadogBaseUrl = 'https://us5.datadoghq.com/logs';
  const url = new URL(datadogBaseUrl);
  url.searchParams.set('query', `@appointmentId:${appointmentId}`);

  // Set default time range to the appointment's creation time to now
  if (appointment) {
    const datadogRangeStart = new Date(appointment.createdAt).getTime();
    const datadogRangeEnd = Date.now();
    url.searchParams.set('from_ts', datadogRangeStart.toString());
    url.searchParams.set('to_ts', datadogRangeEnd.toString());
    url.searchParams.set('live', 'false'); // Disable live tailing
  }

  const datadogUrl = url.toString();

  const langfuseUrl = getLangfuseSessionUrlForAppointment(
    appointmentId,
    langfuseBaseUrl,
    langfuseProjectId,
  );

  return (
    <div>
      <Stack align="center" justify="between" gap={2} direction="horizontal">
        <Stack justify="end" fullWidth={false} gap={2} direction="horizontal">
          <div className="my-2 py-2">
            <Heading>{appointment?.name}</Heading>
          </div>
          <CopyTextButton copyText={appointmentId} />
        </Stack>
        <Stack
          justify="end"
          wrap
          fullWidth={false}
          gap={2}
          direction="horizontal"
        >
          <OpenInAppButton appointmentId={appointmentId} tenantId={tenantId} />
          <OpenInReviewButton
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
          <LinkButton variant="outline" href={datadogUrl}>
            <Icon name="Bone" size="auto" />
            View Datadog Logs
          </LinkButton>
          {langfuseUrl && (
            <LinkButton variant="outline" href={langfuseUrl}>
              <Icon name="ExternalLink" size="auto" />
              View Langfuse Traces
            </LinkButton>
          )}
        </Stack>
      </Stack>
      <TabNav
        tabs={[
          {
            name: 'Details',
            link: 'details',
          },
          {
            name: 'Summary and Transcript',
            link: 'artifacts',
          },
          {
            name: 'Files',
            link: 'files',
          },
          {
            name: 'Live vs Batch Transcript Comparison',
            link: 'transcript-comparison',
          },
          {
            name: 'Source Artifacts',
            link: 'source-artifacts',
          },
        ]}
      />
      <div className="mt-4">
        <Suspense fallback={<LoadingIndicator size="xl" />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
};
